import { useState } from 'react';
import { usePortfolioData } from '@/hooks/use-portfolio-data';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from "@/hooks/use-toast";
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import * as Icons from 'lucide-react';

interface ContactForm {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export function Contact() {
  const { data: portfolioData, isLoading } = usePortfolioData();
  const { toast } = useToast();
  
  const [contactForm, setContactForm] = useState<ContactForm>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const DynamicIcon = ({ name }: { name: string }) => {
    const LucideIcon = (Icons as any)[name] || Icons.Circle;
    return <LucideIcon className="h-5 w-5" />;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const submitContactForm = (e: React.FormEvent) => {
    e.preventDefault();
    
    // In a real implementation, we would send this to a backend
    toast({
      title: "Message Sent",
      description: "Thank you for your message! I'll get back to you soon.",
    });
    
    // Reset form
    setContactForm({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  if (isLoading) {
    return (
      <section id="contact" className="py-20 bg-white dark:bg-slate-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Skeleton className="h-10 w-40 mx-auto mb-4" />
            <Skeleton className="w-16 h-1.5 mx-auto rounded-full mb-4" />
            <Skeleton className="h-5 w-96 mx-auto" />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-72 rounded-xl" />
              <Skeleton className="h-64 rounded-xl" />
            </div>
            
            <div className="lg:col-span-3">
              <Skeleton className="h-96 rounded-xl" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!portfolioData) return null;

  return (
    <section id="contact" className="py-20 bg-white dark:bg-slate-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Contact Me</h2>
          <div className="w-16 h-1.5 bg-primary mx-auto rounded-full"></div>
          <p className="text-gray-600 dark:text-gray-300 mt-4 max-w-2xl mx-auto">
            Have a project in mind or want to discuss potential opportunities? Get in touch.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <h3 className="text-xl font-semibold">Contact Information</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start">
                  <div className="bg-primary/10 rounded-full p-3 text-primary mr-4">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</h4>
                    <a 
                      href={`mailto:${portfolioData.contact.email}`} 
                      className="text-gray-800 dark:text-gray-200 hover:text-primary"
                    >
                      {portfolioData.contact.email}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-primary/10 rounded-full p-3 text-primary mr-4">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</h4>
                    <a 
                      href={`tel:${portfolioData.contact.phone}`} 
                      className="text-gray-800 dark:text-gray-200 hover:text-primary"
                    >
                      {portfolioData.contact.phone}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-primary/10 rounded-full p-3 text-primary mr-4">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</h4>
                    <p className="text-gray-800 dark:text-gray-200">
                      {portfolioData.contact.location}
                    </p>
                  </div>
                </div>
              
                <div className="mt-8">
                  <h4 className="text-md font-medium mb-3">Connect with me</h4>
                  <div className="flex space-x-3">
                    {portfolioData.socialLinks.map((link) => (
                      <a 
                        key={link.url}
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        aria-label={link.name}
                        className="w-10 h-10 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                      >
                        <DynamicIcon name={link.icon} />
                      </a>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="rounded-xl shadow-md overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1487017159836-4e23ece2e4cf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&h=400" 
                alt="Workspace environment" 
                className="w-full h-auto"
              />
            </div>
          </div>
          
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <h3 className="text-xl font-semibold">Send Me a Message</h3>
              </CardHeader>
              
              <CardContent>
                <form onSubmit={submitContactForm} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input 
                        id="name" 
                        name="name"
                        placeholder="Your name" 
                        value={contactForm.name}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input 
                        id="email" 
                        name="email"
                        type="email" 
                        placeholder="Your email" 
                        value={contactForm.email}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input 
                      id="subject" 
                      name="subject"
                      placeholder="Message subject" 
                      value={contactForm.subject}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea 
                      id="message" 
                      name="message"
                      rows={6} 
                      placeholder="Your message" 
                      value={contactForm.message}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  
                  <Button type="submit" className="w-full gap-2">
                    <Send className="h-4 w-4" />
                    <span>Send Message</span>
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
